// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type EntityId, type PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  type BrokerPosition,
  type BrokerPositionsAssetType,
  type BrokerPositionsState,
  type BrokerState,
  type BrokerSummaryState,
} from '@shared/data-access/interfaces/redux/broker';
import { type MyInvestments } from '@shared/data-access/interfaces/common/my-investment';
import { type AccountInfo } from '@shared/data-access/interfaces/api/brokerage/account';
import { accountApi } from '@advisor-app/store/api/brokerage/accounts-api';
import { accountsEntityAdapter, positionsEntityAdapter } from './broker-selectors';
import { portfolioApi } from '@advisor-app/store/api/brokerage/portfolio-api';

export const initialState: BrokerState = {
  broker: '',
  account: { accountId: '', name: '', currency: '' },
  openLinkAccountModal: false,
  accountType: 'PAPER',
  positions: positionsEntityAdapter.getInitialState(),
  summary: {
    cash: 0,
    etf: 0,
    mutual_fund: 0,
    other: 0,
    aum: 0,
    stock: 0,
    equity: 0,
    buyingPower: 0,
  },
  investmentsTableData: [],
  accounts: accountsEntityAdapter.getInitialState({ loading: false }),
};

export const brokerSlice = createSlice({
  name: 'broker',
  initialState: () => initialState,
  reducers: {
    setBroker: (state, { payload }: PayloadAction<string>) => {
      state.broker = payload;
    },
    setBrokerAccount: (
      state,
      {
        payload,
      }: PayloadAction<Pick<BrokerState, 'account' | 'accountType' | 'broker'>>,
    ) => {
      state.broker = payload.broker;
      state.accountType = payload.accountType;
      state.account = payload.account;
    },
    setAccount: (state, { payload }: PayloadAction<BrokerState['account']>) => {
      state.account = payload;
    },
    setAccountId: (state, { payload }: PayloadAction<string>) => {
      state.account.accountId = payload;
    },
    setAccountName: (state, { payload }: PayloadAction<string>) => {
      state.account.name = payload;
    },
    setAccountType: (
      state,
      { payload }: PayloadAction<BrokerState['accountType']>,
    ) => {
      state.accountType = payload;
    },
    setOpenLinkAccountModal: (state, { payload }: PayloadAction<boolean>) => {
      state.openLinkAccountModal = payload;
    },
    updateSummary: (state, action: PayloadAction<Partial<BrokerSummaryState>>) => {
      state.summary = { ...state.summary, ...action.payload };
    },
    setAllPositions: (
      state,
      {
        payload,
      }: PayloadAction<Partial<Record<BrokerPositionsAssetType, BrokerPosition[]>>>,
    ) => {
      const entities = Object.fromEntries(
        Object.entries(payload).map(([type, value]) => [
          type,
          { asset: type as BrokerPositionsAssetType, positions: value },
        ]),
      );

      positionsEntityAdapter.setAll(state.positions, entities);
    },
    setPositionsById: (
      state,
      {
        payload: [id, values],
      }: PayloadAction<[id: BrokerPositionsAssetType, values: BrokerPosition[]]>,
    ) => {
      positionsEntityAdapter.setOne(state.positions, {
        asset: id,
        positions: values,
      });
    },
    setInvestmentsTableData: (state, action: PayloadAction<MyInvestments[]>) => {
      state.investmentsTableData = action.payload;
    },
    setAccounts: (state, { payload }: PayloadAction<AccountInfo[]>) => {
      accountsEntityAdapter.setAll(state.accounts, payload);
    },
    setAccountsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.accounts.loading = payload;
    },
    resetBroker: () => initialState,
  },
  extraReducers: builder => {
    /* istanbul ignore next */
    builder
      .addMatcher(
        accountApi.endpoints.accountAll.matchFulfilled,
        (state, { payload }) => {
          const currentAccount = state.account.accountId;

          brokerSlice.caseReducers.setAccounts(state, {
            type: brokerSlice.actions.setAccount.type,
            payload: payload,
          });

          const accountInfo = payload.find(
            ({ account_id }) => account_id === currentAccount,
          );

          brokerSlice.caseReducers.updateSummary(state, {
            type: brokerSlice.actions.updateSummary.type,
            payload: {
              aum: accountInfo?.aum || 0,
              buyingPower: accountInfo?.buying_power || 0,
              cash: accountInfo?.cash || 0,
              equity: accountInfo?.investment || 0,
            },
          });
        },
      )
      .addMatcher(
        portfolioApi.endpoints.getPositions.matchFulfilled,
        (state, { payload }) => {
          const assets = new Map(
            payload.map(position => [position.ticker, position]),
          );

          const positions = Object.fromEntries(
            Object.entries(state.positions.entities).map(([key, position]) => [
              key,
              {
                asset: position?.asset,
                positions: position?.positions.map(asset => {
                  return {
                    ...asset,
                    currency: assets.get(asset.ticker)?.currency,
                    investment: assets.get(asset.ticker)?.outstanding_balance ?? 0,
                    unrealized_capital_gains:
                      assets.get(asset.ticker)?.unrealized_capital_gains ?? 0,
                    price: assets.get(asset.ticker)?.price ?? 0,
                    total_gain_loss: [
                      assets.get(asset.ticker)?.unrealized_capital_gains,
                      0,
                    ],
                  };
                }),
              },
            ]),
          ) as Record<EntityId, BrokerPositionsState>;

          const investmentsTableData = state.investmentsTableData.map(asset => {
            const subRows = asset.subRows.map(subRow => ({
              ...subRow,
              currency: assets.get(subRow.ticker)?.currency,
              investment: assets.get(subRow.ticker)?.outstanding_balance ?? 0,
              unrealized_capital_gains:
                assets.get(subRow.ticker)?.unrealized_capital_gains ?? 0,
              price: assets.get(subRow.ticker)?.price ?? 0,
              total_gain_loss: [
                assets.get(subRow.ticker)?.unrealized_capital_gains,
                0,
              ],
            }));

            return {
              ...asset,
              investment: subRows.reduce((acc, row) => acc + row.investment, 0),
              total_gain_loss: [
                subRows.reduce((acc, row) => acc + row.unrealized_capital_gains, 0),
                0,
              ],
              subRows,
            };
          }) as MyInvestments[];

          positionsEntityAdapter.setAll(state.positions, positions);

          state.investmentsTableData = investmentsTableData;
        },
      );
  },
});

export const {
  actions: {
    resetBroker,
    setAccount,
    setAccountId,
    setAccountName,
    setAccountType,
    setAllPositions,
    setBroker,
    setBrokerAccount,
    setOpenLinkAccountModal,
    setPositionsById,
    setInvestmentsTableData,
    updateSummary,
    setAccounts,
    setAccountsLoading,
  },
  reducer,
} = brokerSlice;
